<template>
	<!-- Figma 14254:161609 (drawer) / 14254:162862 (page): white, radius 36, 24 in from the sides; each block
	     20 above and below; the dividers run between the 24px insets, not edge to edge. -->
	<LayoutFlex
		class="order-info-card"
		padding="0 var(--size-24)"
		gap="0"
		background="--background-white"
		corner-radius="var(--size-36)"
	>
		<LayoutFlex
			v-if="$slots.header || title"
			padding="var(--size-20) 0"
			gap="var(--size-4)"
			:box-shadow="hasBody ? KOVA.divider : 'none'"
		>
			<slot name="header">
				<Text font="--text-18-22-semi" color="--text-dark-primary" role="heading" aria-level="2">{{ title }}</Text>
				<Text v-if="subtitle" font="--text-14-20-med" :color="subtitleColor">{{ subtitle }}</Text>
			</slot>
		</LayoutFlex>

		<LayoutFlex v-if="$slots.default" padding="var(--size-20) 0" gap="0">
			<slot />
		</LayoutFlex>

		<dl
			v-if="rows.length"
			class="order-info-card__rows"
			:class="stacked ? 'order-info-card__rows--stacked' : 'order-info-card__rows--inline'"
		>
			<div
				v-for="(row, index) in rows"
				:key="row.key"
				class="order-info-card__row"
				:style="stacked && index < rows.length - 1 ? { boxShadow: KOVA.divider } : undefined"
			>
				<dt>
					<Text font="--text-14-20-med" color="--text-dark-secondary">{{ row.label }}</Text>
				</dt>
				<dd class="order-info-card__value">
					<!-- A phone stacks the lines (Figma 14254:162862: "1200 Example Street" over "Austin, TX 78701");
					     the drawer reads them as one line, right-aligned (14254:161609). -->
					<span v-if="stacked" class="order-info-card__lines">
						<Text v-for="(line, i) in row.lines" :key="i" font="--text-18-22-med" color="--text-dark-primary">
							{{ line }}
						</Text>
					</span>
					<Text v-else font="--text-18-22-med" color="--text-dark-primary" text-align="right">
						{{ row.lines.join(', ') }}
					</Text>
					<slot name="trailing" :row="row" />
				</dd>
			</div>
		</dl>
	</LayoutFlex>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'
import { LayoutFlex, Text } from '@simple-life-app/growth-ui-kova'
import { KOVA } from '@/designTokens'

/**
 * One card of the order details (Figma "Top card", "Delivery info", "Shipping details", "Payment details"):
 * a title (a level-2 heading) with an optional subtitle — or the `header` slot — a divider, then the
 * default slot's body and/or label / value `rows`. `stacked` is the phone layout (label over value, a
 * divider between rows); otherwise the label sits left and the value right on one line, rows 32 apart.
 * The `trailing` slot draws beside a row's value (the card brand mark).
 *
 * The values are the customer's own data (address, name, phone): they go in text only — never in a
 * `title`, `aria-label`, `alt`, `id` or `class` (docs/architecture/sentry.md → PHI/PII hardening).
 */

export interface OrderInfoRow {
	/** A PII-free key ("address", "recipient", "card"). */
	key: string
	label: string
	lines: string[]
}

const props = withDefaults(
	defineProps<{
		title?: string
		subtitle?: string
		/** Figma greys a delivery estimate at 55% (`text/black/secondary`), "1 address" at 60% — the default. */
		subtitleColor?: string
		rows?: OrderInfoRow[]
		stacked?: boolean
	}>(),
	{ title: undefined, subtitle: undefined, subtitleColor: '--text-dark-secondary', rows: () => [], stacked: false },
)

defineSlots<{
	header?: () => unknown
	default?: () => unknown
	trailing?: (props: { row: OrderInfoRow }) => unknown
}>()

const slots = useSlots()

/** The divider under the header only when something follows it. */
const hasBody = computed(() => Boolean(slots.default) || props.rows.length > 0)
</script>

<style scoped>
.order-info-card__rows {
	display: flex;
	flex-direction: column;
	margin: 0;
}

/* The drawer (14254:161645): the first value 20 under the divider, the next 32 lower, 20 under the last. */
.order-info-card__rows--inline {
	gap: var(--size-32);
	padding: var(--size-20) 0;
}

.order-info-card__rows--inline .order-info-card__row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--size-16);
}

/* A phone (14254:162914): label over value 8 apart, every row 20 above and below, a divider between rows. */
.order-info-card__rows--stacked .order-info-card__row {
	display: flex;
	flex-direction: column;
	gap: var(--size-8);
	padding: var(--size-20) 0;
}

.order-info-card__row dt {
	flex: none;
}

.order-info-card__value {
	display: flex;
	align-items: center;
	gap: var(--size-12);
	min-width: 0;
	margin: 0;
	overflow-wrap: anywhere;
}

.order-info-card__rows--inline .order-info-card__value {
	justify-content: flex-end;
}

.order-info-card__rows--stacked .order-info-card__value {
	justify-content: space-between;
}

.order-info-card__lines {
	display: flex;
	flex-direction: column;
	min-width: 0;
}
</style>
